import { read, TABLE } from '../data/db';
import { hypotheses, matches, teams } from '../data/repos';
import { validateBoard } from '../domain/board';
import type { ConfirmLockCommand, SetStatusOverrideCommand } from '../domain/commands';
import type { HypothesisPhase, MatchRow, Status, StatusOverride } from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';

/**
 * LIVE-1 … LIVE-12, LOCK-4, LOCK-5 — the **I/O half** of the locked board.
 *
 * §1.2 forbids `ui/` from importing `data/`, so every tile the grid renders arrives through here and
 * every write leaves through `dispatch`. The *decisions* are not here: `domain/order.ts` owns INV-11,
 * `domain/lockedBoard.ts` owns the grid and DISC-7's gate on this surface, `domain/board.ts` owns
 * allocation, `domain/lock.ts` owns LOCK-2/LOCK-3.
 *
 * ── The tap path is `app/discovery.ts`'s, deliberately ──────────────────────
 *
 * `prepareIncrement`, `commitIncrement` and `canCapture` are **not re-implemented here**. §5.1 is one
 * path and both capture surfaces walk it; LOCK-5's *"a Discovery visit MUST NOT itself change any
 * tile's position or status"* is true precisely because Discovery and the board dispatch the **same**
 * `Increment` command, whose `Decision<CaptureOp>` cannot compile a position write (INV-4). A second
 * copy of step 4 here would be a second place for the pre-built row to drift from the command it
 * rides on, and it would quietly cost that proof.
 *
 * The same argument covers LOCK-3's review screen: it reads through `loadDiscovery`, because the
 * candidates it ranks are exactly Discovery's rows with exactly DISC-4's sort keys, and LOCK-2 is
 * defined as `topN` over that same total order.
 *
 * ── Why the board's read does not touch `observations` ───────────────────────
 *
 * Discovery's load reads the ledger once for DISC-4's `firstObservationCreatedAt` tie-break. The
 * board has no such term — `lockedOrder`'s parameter type declares no count at all (LIVE-12) — so
 * the ledger is not in this transaction. That is not an optimisation to be reversed later: putting
 * the match's whole Observation log in front of the surface whose budget argument is NFR-1's would
 * be a regression on the hottest screen in the app.
 */

/** A tile as the grid renders it: INV-11's order keys, DISC-3's count badge, §6.5's visuals. */
export interface BoardTileView {
  readonly id: string;
  readonly text: string;
  /**
   * The tile's own phase (seven values). Added for B-15's hypothesis detail sheet, which names
   * the phase a pattern belongs to; projected straight off the stored column beside the existing
   * fields — additive, ranked by nothing, and A's board ignores it. LIVE-12 is untouched:
   * `lockedOrder`'s parameter type still declares no count and no phase, so the board's render
   * order cannot consult either.
   */
  readonly phase: HypothesisPhase;
  /** INV-11's dismissal dimension. The **only** field of this view the render order may consult. */
  readonly statusOverride: StatusOverride;
  /** Non-null on every tile: a `null` position is a list-only hypothesis and not a tile (INV-4). */
  readonly boardPosition: number;
  /** LIVE-2/LIVE-3's large count. Rendered, never ranked (LIVE-12). */
  readonly cachedCount: number;
  readonly status: Status;
  /** §5.4 — LIVE-6's window is a predicate over this committed column. The affordance is U-10's. */
  readonly lastIncrementTs: number | null;
  /** PRE-5 / §6.5 — the non-status carry badge. Affects neither status nor order. */
  readonly carriedStatus: Status | null;
  readonly carriedCount: number | null;
}

export interface LockedBoardContext {
  readonly match: MatchRow;
  /** §5.6's top zone shows *"clock · opponent · half"*, and the opponent is a name, not an id. */
  readonly opponent: string;
  readonly tiles: readonly BoardTileView[];
  /**
   * INV-5 / LIVE-8 / §6.2 — `validateBoard`'s verdict on what was actually read, or `null` when the
   * board is legal.
   *
   * Surfaced rather than swallowed (NFR-8). A board that has somehow reached nine tiles or a middle
   * gap still renders — hiding a tile would make the breach invisible on the one screen that shows
   * the board — but the surface says so instead of drawing a grid that quietly disagrees with the
   * allocation rule.
   */
  readonly integrity: string | null;
}

/**
 * Everything the locked board needs, from **one** readonly transaction.
 *
 * One transaction because the tiles, the opponent name and the match state must describe the same
 * instant: a board read against a match row from before a transition would render the wrong
 * transition control and the wrong clock beside tiles that had already moved on.
 *
 * @returns `undefined` when the match has vanished — a normal outcome after a cascade, and the
 *   caller navigates rather than rendering an empty grid.
 * @throws {StorageError} on a read failure. Not swallowed: an empty board and a failed read look
 *   identical to the user, and only one of them is worth trying again.
 */
export async function loadLockedBoard(matchId: string): Promise<LockedBoardContext | undefined> {
  return read(
    [TABLE.matches, TABLE.teams, TABLE.hypotheses],
    async (): Promise<LockedBoardContext | undefined> => {
      const match = await matches.getMatch(matchId);
      if (match === undefined) return undefined;

      const positioned = await hypotheses.listPositioned(matchId);
      const shape = validateBoard(positioned);

      return {
        match,
        // An unnamed opponent is a data fault, not a rendering decision — but the capture surface is
        // the wrong place to refuse over it, so it degrades to a label rather than an empty header.
        opponent: (await teams.getTeam(match.teamId))?.name ?? 'Opponent',
        tiles: positioned.map(toTileView),
        integrity: shape.ok ? null : shape.message,
      };
    },
    'loadLockedBoard',
  );
}

/**
 * `listPositioned` reads the `[matchId+boardPosition]` index, whose entries omit null components
 * (§3.4) — so every row it returns holds a position. The fallback keeps the projection total rather
 * than asserting: a `-1` would be visibly wrong in `validateBoard`'s report, where a thrown cast
 * would take the whole surface down mid-half.
 */
function toTileView(row: {
  readonly id: string;
  readonly text: string;
  readonly phase: HypothesisPhase;
  readonly statusOverride: StatusOverride;
  readonly boardPosition: number | null;
  readonly cachedCount: number;
  readonly status: Status;
  readonly lastIncrementTs: number | null;
  readonly carriedStatus: Status | null;
  readonly carriedCount: number | null;
}): BoardTileView {
  return {
    id: row.id,
    text: row.text,
    phase: row.phase,
    statusOverride: row.statusOverride,
    boardPosition: row.boardPosition ?? -1,
    cachedCount: row.cachedCount,
    status: row.status,
    lastIncrementTs: row.lastIncrementTs,
    carriedStatus: row.carriedStatus,
    carriedCount: row.carriedCount,
  };
}

/**
 * LOCK-4 — the confirm at the end of LOCK-3's review.
 *
 * `orderedHypothesisIds` is the board order the user arranged, and `handleConfirmLock` assigns
 * `0..k−1` in exactly that order (§6.2). Everything that could go wrong — a second lock, a ninth
 * tile, a hypothesis from another match — is refused by the handler, the repo and the unique index;
 * this function adds no guard of its own, because a fourth one at the UI boundary would be the one
 * that looked authoritative and was not.
 */
export function confirmLock(
  matchId: string,
  orderedHypothesisIds: readonly string[],
): Promise<DispatchResult> {
  const command: ConfirmLockCommand = { type: 'ConfirmLock', matchId, orderedHypothesisIds };
  return dispatch(command);
}

/**
 * LIVE-5 — swipe-left sets `disproved`, and the same gesture clears it.
 *
 * The value is decided by `domain/lockedBoard.swipeLeftOverride`, not here: what to write is a
 * requirement, and this is the wire. `SetStatusOverride` declares `Decision<CaptureOp>`, so this
 * path **cannot compile** a `boardPosition` write — INV-4's *"never changed by … status changes"* is
 * a type error rather than a promise, which is what makes LIVE-11's *"the tile returns to its
 * original slot"* hold with no code to hold it.
 */
export function setTileOverride(
  matchId: string,
  hypothesisId: string,
  statusOverride: StatusOverride,
): Promise<DispatchResult> {
  const command: SetStatusOverrideCommand = {
    type: 'SetStatusOverride',
    matchId,
    hypothesisId,
    statusOverride,
  };
  return dispatch(command);
}
